import { invalidate, ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import {
    BoxGeometry,
    Color,
    DoubleSide,
    type InstancedMesh,
    type Mesh,
    MeshBasicMaterial,
    MeshStandardMaterial,
    Object3D,
    SphereGeometry,
    Vector3
} from 'three';
import { useShallow } from 'zustand/shallow';

import { LayerType } from 'app/modules/wallforming/components/scenes/camera-controls/types/layerType';
import { disposeObject } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { PRIMARY_COLOR_ACCENT_HEX } from 'shared/constants';

import type { PrefabModel } from 'api/models';

interface ArticleInsertionPointsProps {
    articleId: string;
    articleRepresentation: PrefabModel;
}

const originPosition = new Vector3(0, 0, 0);

const defaultColor = new Color(PRIMARY_COLOR_ACCENT_HEX);
const hoverColor = new Color('#478dd3');

const colliderGeometryDefault = new BoxGeometry(0.6, 0.6, 0.6);
const colliderGeometryOrigin = new SphereGeometry(0.4, 8, 8);
const colliderMaterial = new MeshBasicMaterial({
    depthTest: false,
    depthWrite: false,
    side: DoubleSide,
    transparent: true
});

export const ArticleInsertionPoints = ({ articleId, articleRepresentation }: ArticleInsertionPointsProps) => {
    const colliderMeshRef = useRef<InstancedMesh | null>(null);
    const originMaterialRef = useRef<MeshStandardMaterial | null>(null);
    const originMeshRef = useRef<Mesh | null>(null);
    const dummyMeshRef = useRef<Object3D>(new Object3D());

    const hoverdIndexRef = useRef<number | undefined>(undefined);

    const insertionPointRef = useRef<Vector3>(usePlacementStore.getState().insertionPoint ?? new Vector3(0, 0, 0));

    const { showInsertPointSelection, extractLocalColliders, setInsertionPoint, isArticleInsertionEnabled } =
        usePlacementStore(
            useShallow((state) => ({
                showInsertPointSelection: state.showInsertPointSelection,
                extractLocalColliders: state.extractLocalColliders,
                setInsertionPoint: state.setInsertionPoint,
                isArticleInsertionEnabled: state.isArticleInsertionEnabled
            }))
        );

    const camera = useThree((state) => state.camera);
    const gl = useThree((state) => state.gl);

    const insertionPoints = useMemo(() => {
        if (!articleId || !articleRepresentation) {
            return [];
        }

        let localColliders: Vector3[] = [];
        if (articleRepresentation.children?.length > 0) {
            for (const child of articleRepresentation.children) {
                localColliders.push(...extractLocalColliders(child.name, child));
            }
        } else {
            localColliders = extractLocalColliders(articleRepresentation.name, articleRepresentation);
        }

        return localColliders;
    }, [articleId, articleRepresentation, extractLocalColliders]);

    const colliderGeometry = useMemo(
        () => (insertionPoints.length === 1 ? colliderGeometryOrigin : colliderGeometryDefault),
        [insertionPoints.length]
    );

    const handleOnInsertPointSelection = (event: ThreeEvent<PointerEvent>, isOrigin: boolean) => {
        const index = !isOrigin ? event.instanceId : undefined;
        const selectedEntry = index !== undefined ? insertionPoints[index] : undefined;
        insertionPointRef.current = selectedEntry ?? originPosition;
        setInsertionPoint(insertionPointRef.current.x, insertionPointRef.current.y, insertionPointRef.current.z);
    };

    const updateColliderColor = (index: number, color: Color) => {
        if (index === undefined || !colliderMeshRef.current) {
            return;
        }

        colliderMeshRef.current.setColorAt(index, color);
        if (colliderMeshRef.current.instanceColor) {
            colliderMeshRef.current.instanceColor.needsUpdate = true;
        }
    };

    const handlePointerEvent = (event: ThreeEvent<PointerEvent>, isPointerOver: boolean, isOrigin: boolean) => {
        if (isOrigin && originMaterialRef.current) {
            originMaterialRef.current.color.set(isPointerOver ? hoverColor : defaultColor);
            originMaterialRef.current.needsUpdate = true;
            hoverdIndexRef.current = isPointerOver ? insertionPoints.length : undefined;
        } else {
            const index = event.instanceId;
            if (index === undefined) {
                return;
            }

            hoverdIndexRef.current = isPointerOver ? index : undefined;
            updateColliderColor(index, isPointerOver ? defaultColor : hoverColor);
        }

        const cursorStyle = isPointerOver ? 'pointer' : isArticleInsertionEnabled() ? 'move' : 'auto';
        gl.domElement.style.setProperty('cursor', cursorStyle);

        invalidate();
    };

    const updateColliderMatrices = () => {
        if (!colliderMeshRef.current) {
            return;
        }

        insertionPoints.forEach((point, i) => {
            if (!colliderMeshRef.current) {
                return;
            }

            const isSelected =
                insertionPoints.length === 1 || point.equals(insertionPointRef.current) || i === hoverdIndexRef.current;
            const scale = isSelected ? 1.3 : 1;

            dummyMeshRef.current.position.set(point.x, point.y, point.z);
            dummyMeshRef.current.scale.setScalar((20 / camera.zoom) * scale);
            dummyMeshRef.current.updateMatrix();

            colliderMeshRef.current.setMatrixAt(i, dummyMeshRef.current.matrix);
            colliderMeshRef.current.setColorAt(i, isSelected ? hoverColor : defaultColor);
        });

        if (colliderMeshRef.current.instanceColor) {
            colliderMeshRef.current.instanceColor.needsUpdate = true;
        }

        colliderMeshRef.current.instanceMatrix.needsUpdate = true;
    };

    const updateOrigin = () => {
        if (!originMaterialRef.current || !originMeshRef.current) {
            return;
        }

        const isSelected =
            insertionPointRef.current.equals(originPosition) || hoverdIndexRef.current === insertionPoints.length;

        const scale = isSelected ? 1.2 : 1;

        originMeshRef.current.scale.setScalar((20 / camera.zoom) * scale);
        originMeshRef.current.updateMatrix();

        originMaterialRef.current.color.set(isSelected ? hoverColor : defaultColor);
        originMaterialRef.current.needsUpdate = true;
    };

    useFrame(() => {
        updateColliderMatrices();
        updateOrigin();
    });

    useEffect(() => {
        const colliderMesh = colliderMeshRef.current;

        return () => {
            disposeObject(colliderMesh);
        };
    }, [colliderMeshRef, articleId]);

    const handleColliderMeshRef = (colliderMesh: InstancedMesh | null) => {
        if (colliderMesh) {
            colliderMeshRef.current = colliderMesh;
            updateColliderMatrices();
        }
    };

    return (
        <>
            <instancedMesh
                ref={handleColliderMeshRef}
                args={[colliderGeometry, colliderMaterial, insertionPoints.length]}
                layers={[LayerType.RAYCAST]}
                renderOrder={100}
                visible={showInsertPointSelection}
                onPointerDown={(e) => handleOnInsertPointSelection(e, false)}
                onPointerOut={(e) => handlePointerEvent(e, false, false)}
                onPointerOver={(e) => handlePointerEvent(e, true, false)}
            />

            {insertionPoints.length !== 1 && (
                <mesh
                    ref={originMeshRef}
                    layers={[LayerType.RAYCAST]}
                    position={originPosition}
                    renderOrder={100}
                    visible={showInsertPointSelection}
                    onPointerDown={(e) => handleOnInsertPointSelection(e, true)}
                    onPointerOut={(e) => handlePointerEvent(e, false, true)}
                    onPointerOver={(e) => handlePointerEvent(e, true, true)}
                >
                    <sphereGeometry args={[0.4, 8, 8]} />
                    <meshStandardMaterial
                        ref={originMaterialRef}
                        depthTest={false}
                        depthWrite={false}
                        side={DoubleSide}
                        transparent
                    />
                </mesh>
            )}
        </>
    );
};
